// @ts-strict-ignore
import type {
  dashboardApiEndpoints,
  DashboardApiEndpoints,
  EndpointMethod,
  ErrorOf,
  FilteredDashboardApiEndpointKeys,
  MutationVariablesOf,
  ResultOf,
  UseDashboardApiMutationOptions,
} from "@dashboardApi/apis";
import { useMutation } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { useInEditorDomain } from "@services/useInEditorDomain";

import { useDashboardApi } from "./useDashboardApi";
import { useDashboardApiMutationErrorToast } from "./useDashboardApiMutationErrorToast";
import { useEditorDomainApiMutationFetcher } from "./useEditorDomainApiMutationFetcher";

/**
 * Based on the endpoint specified, this returns the `useMutation` result of passing in the fetcher function of the specified method.
 *
 * An optional transformer can be used to take input and transform it into the parameters expected by the fetcher function.
 * @template Method `"GET"` `"PUT"` `"POST"` `"DELETE"` or `"PATCH"`
 * @template Endpoint the endpoint name. Example: `"/engagement/get_changelogs"`
 * @template MutationFetcher the fetcher function for the specified endpoint/method
 * @template TData the type returned from the mutate function returned from `useMutation`. This is either the result of the fetcher function, or the transformer
 * @template TError the error type used in both options and `error` returned from `useMutation`
 * @template TVariables the argument to the `mutate`/`mutateAsync` function returned from `useMutation`
 * @template TContext the return value of `onMutate` which is inferred. See https://tanstack.com/query/v3/docs/react/reference/useMutation
 */
export const useDashboardApiMutation = <
  Method extends Exclude<EndpointMethod, "GET" | "POSTWS">,
  Endpoint extends FilteredDashboardApiEndpointKeys<typeof dashboardApiEndpoints, Method>,
  TData = ResultOf<Method, Endpoint>,
  TError = ErrorOf<Method, Endpoint>,
  TVariables = MutationVariablesOf<Method, Endpoint>,
  TContext = unknown,
>(
  method: Method,
  endpoint: Endpoint,
  {
    transformer,
    options,
    disableDefaultErrorToast,
  }: UseDashboardApiMutationOptions<
    typeof dashboardApiEndpoints,
    Method,
    Endpoint,
    TData,
    TError,
    TVariables,
    TContext
  > = {}
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const { [method]: defaultFetcherFunction } = useDashboardApi(endpoint as unknown as DashboardApiEndpoints);
  const defaultOnError = useDashboardApiMutationErrorToast();
  const inEditorDomain = useInEditorDomain();
  const postMessageFetcher = useEditorDomainApiMutationFetcher(endpoint, method);
  const fetcherFunction = (args) => {
    return (defaultFetcherFunction as any)(args, inEditorDomain ? postMessageFetcher : undefined);
  };
  return useMutation<TData, TError, TVariables, TContext>(
    transformer
      ? async (
          ...args: typeof fetcherFunction extends (...args: any[]) => any
            ? Parameters<typeof fetcherFunction>
            : never
        ) => transformer(await (fetcherFunction as any)(args))
      : (fetcherFunction as any),
    {
      ...options,
      onError:
        options?.onError === undefined
          ? // for endpoints that handle the error using `isError` or try/catch with `mutateAsync`, false disabled `onError` entirely
            disableDefaultErrorToast
            ? undefined
            : // for endpoints that don't define error handling, the default toast error handler is used
              defaultOnError
          : // otherwise, explicitly passed `onError` are used
            options?.onError,
    }
  );
};
